import http from './http'
import { getMediaOrigin } from './config'

const LIST_PAGE_PARAMS = { page: 1, per_page: 999 }
let homeNewsCache = null
let homeNewsPromise = null
let newsCategoriesCache = null
let newsCategoriesPromise = null
const newsByCategoryCache = new Map()
const newsByCategoryPromise = new Map()
const newsDetailCache = new Map()
const newsDetailPromise = new Map()

function resolveStorageUrl(path) {
  if (!path || typeof path !== 'string') return ''
  const t = path.trim()
  if (!t) return ''
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:')) {
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '')
    return `${base}/${rest.replace(/^\//, '')}`
  }
  if (t.startsWith('/storage/') || t.startsWith('storage/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  if (t.startsWith('/uploads/') || t.startsWith('uploads/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  return `${base}/storage/${t.replace(/^\//, '')}`
}

function resolveHtmlUrl(path) {
  if (!path || typeof path !== 'string') return path
  const t = path.trim()
  if (!t) return t
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:') || t.startsWith('#')) return t
  const base = getMediaOrigin().replace(/\/$/, '')
  return `${base}/${t.replace(/^\//, '')}`
}

function normalizeRichHtml(raw) {
  if (typeof raw !== 'string') return ''
  const html = raw.trim()
  if (!html) return ''
  return html.replace(/(src|href|data-href)=["']([^"']+)["']/gi, (_m, attr, url) => {
    const resolved = resolveHtmlUrl(url)
    return `${attr}="${resolved}"`
  })
}


function unwrapArray(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  const d = payload.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data
  return []
}

function normalizeNewsCategory(row) {
  if (!row || typeof row !== 'object') return null
  const id =
    row.id ??
    row.news_category_id ??
    row.category_id ??
    (row.news_category && typeof row.news_category === 'object'
      ? row.news_category.id
      : undefined)
  if (id == null) return null
  const name =
    row.name ||
    row.title ||
    row.category_name ||
    (row.news_category && (row.news_category.name || row.news_category.title)) ||
    '未命名'
  return {
    id: Number(id) || id,
    name,
    title: row.title || name
  }
}

function normalizeNews(row) {
  if (!row || typeof row !== 'object') return null
  const cover =
    row.cover_photo_path ||
    row.cover ||
    row.image ||
    row.pic ||
    row.image_url ||
    row.imageUrl ||
    ''
  const date = row.date || row.publish_date || row.published_at || row.created_at || ''
  const catId = row.news_category_id ?? row.category_id ?? row.news_categories_id
  const raw =
    row.body ||
    row.content_html ||
    row.html_content ||
    row.content ||
    row.detail ||
    ''
  const s = typeof raw === 'string' ? raw.trim() : ''
  const isHtml = s.length > 0 && /<[a-z][\s\S]*>/i.test(s)
  return {
    id: row.id ?? row.news_id,
    title: row.title || row.name || '资讯',
    subtitle: row.subtitle || row.summary || row.description || '',
    category: row.category || row.category_name || row.type || '公司新闻',
    categoryId: catId != null ? catId : undefined,
    date: typeof date === 'string' ? date.slice(0, 10) : '',
    intro: row.intro || row.summary || row.description || '',
    image: resolveStorageUrl(typeof cover === 'string' ? cover : ''),
    content: !isHtml && s ? s : '',
    contentHtml: isHtml ? normalizeRichHtml(s) : ''
  }
}

function unwrapNewsList(raw) {
  let rows = []
  if (Array.isArray(raw)) {
    rows = raw
  } else if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.news)) rows = raw.news
    else if (Array.isArray(raw.data)) rows = raw.data
    else if (Array.isArray(raw.items)) rows = raw.items
    else if (Array.isArray(raw.list)) rows = raw.list
    else if (Array.isArray(raw.articles)) rows = raw.articles
    else rows = [raw]
  }
  return rows.map(normalizeNews).filter(Boolean)
}

export const FALLBACK_NEWS = [
  {
    id: 1,
    title: '赋能智慧殿堂，共创未来空间',
    subtitle: '新一代智能图书馆终端与管理系统在重点高校落地。',
    category: '公司新闻',
    categoryId: 1,
    date: '2025-11-06',
    intro: '借还效率与入馆体验同步提升，馆员服务更加精准高效。',
    content: ''
  },
  {
    id: 2,
    title: '城市级新馆开馆，智慧化建设跑出加速度',
    subtitle: '城市图书馆综合服务大厅与智慧书库协同运转。',
    category: '行业动态',
    categoryId: 2,
    date: '2025-09-28',
    intro: '构建“15 分钟阅读圈”，提升公共文化服务供给。',
    content: ''
  },
  {
    id: 3,
    title: 'AI 馆员与数据创新应用获行业认可',
    subtitle: 'AI 阅读助手在文旅数字化赛事中获奖。',
    category: '公司新闻',
    categoryId: 1,
    date: '2025-09-26',
    intro: '体现人工智能在公共文化场景中的落地价值。',
    content: ''
  }
]

/** 首项 id 为 all 表示「全部新闻」，与左侧菜单一致 */
export const FALLBACK_NEWS_CATEGORIES = [
  { id: 'all', name: '全部新闻' },
  { id: 1, name: '公司新闻' },
  { id: 2, name: '行业动态' }
]

/**
 * 新闻分类（Laravel 常见：GET /news_categories）
 */
export async function fetchNewsCategories() {
  if (Array.isArray(newsCategoriesCache)) return newsCategoriesCache.map((c) => ({ ...c }))
  if (newsCategoriesPromise) return newsCategoriesPromise.then((list) => list.map((c) => ({ ...c })))

  const allItem = { id: 'all', name: '全部新闻' }
  newsCategoriesPromise = (async () => {
    try {
      const { data } = await http.get('/api/v1/news_categories')
      const raw = data?.data !== undefined ? data.data : data
      const list = unwrapArray({ data: raw }).map(normalizeNewsCategory).filter(Boolean)
      if (list.length) return [allItem, ...list]
    } catch (e) {
      console.warn('[news] 分类接口失败，使用本地分类', e)
    }
    return FALLBACK_NEWS_CATEGORIES.map((c) => ({ ...c }))
  })()
    .then((list) => {
      newsCategoriesCache = list
      return list
    })
    .finally(() => {
      newsCategoriesPromise = null
    })

  return newsCategoriesPromise.then((list) => list.map((c) => ({ ...c })))
}

/**
 * 按左侧分类 id 拉右侧列表；id 为 all / 空 时不带分类参数请求全量
 */
export async function fetchNewsByCategory(categoryId) {
  const id = categoryId
  const key = id == null || id === '' ? 'all' : String(id)
  if (newsByCategoryCache.has(key)) {
    return newsByCategoryCache.get(key).map((n) => ({ ...n }))
  }
  if (newsByCategoryPromise.has(key)) {
    return newsByCategoryPromise.get(key).then((list) => list.map((n) => ({ ...n })))
  }

  if (id == null || id === '' || id === 'all') {
    const req = (async () => {
      try {
        const { data } = await http.get('/api/v1/news', { params: LIST_PAGE_PARAMS })
        const raw = data?.data !== undefined ? data.data : data
        const list = unwrapNewsList(raw)
        if (list.length) return list
      } catch (e) {
        console.warn('[news] 全量列表失败，使用本地数据', e)
      }
      return FALLBACK_NEWS.map((n) => ({ ...n }))
    })()
      .then((list) => {
        newsByCategoryCache.set('all', list)
        return list
      })
      .finally(() => {
        newsByCategoryPromise.delete('all')
      })
    newsByCategoryPromise.set('all', req)
    return req.then((list) => list.map((n) => ({ ...n })))
  }

  const load = async (params) => {
    const { data } = await http.get('/api/v1/news', { params: { ...params, ...LIST_PAGE_PARAMS } })
    const raw = data?.data !== undefined ? data.data : data
    return unwrapNewsList(raw)
  }

  const req = (async () => {
    try {
      return await load({ news_category_id: id, category_id: id })
    } catch (e) {
      console.warn('[news] news 组合参数失败，尝试 news_category_id', id, e)
    }

    try {
      return await load({ news_category_id: id })
    } catch (e) {
      console.warn('[news] news?news_category_id 失败，尝试 category_id', id, e)
    }

    try {
      return await load({ category_id: id })
    } catch (e) {
      console.warn('[news] news?category_id 失败', id, e)
    }

    const cat = FALLBACK_NEWS_CATEGORIES.find((c) => String(c.id) === String(id))
    if (!cat || cat.id === 'all') return FALLBACK_NEWS.map((n) => ({ ...n }))
    return FALLBACK_NEWS.filter((n) => n.category === cat.name).map((n) => ({ ...n }))
  })()
    .then((list) => {
      newsByCategoryCache.set(key, list)
      return list
    })
    .finally(() => {
      newsByCategoryPromise.delete(key)
    })

  newsByCategoryPromise.set(key, req)
  return req.then((list) => list.map((n) => ({ ...n })))
}

export async function fetchNewsList() {
  if (Array.isArray(homeNewsCache)) return homeNewsCache.map((n) => ({ ...n }))
  if (homeNewsPromise) return homeNewsPromise.then((list) => list.map((n) => ({ ...n })))

  homeNewsPromise = fetchNewsByCategory('all')
    .then((list) => {
      homeNewsCache = Array.isArray(list) ? list : []
      return homeNewsCache
    })
    .finally(() => {
      homeNewsPromise = null
    })

  return homeNewsPromise.then((list) => list.map((n) => ({ ...n })))
}

export async function fetchNewsDetail(newsId) {
  const id = newsId
  if (id == null || id === '') return null
  const key = String(id)
  if (newsDetailCache.has(key)) {
    const cached = newsDetailCache.get(key)
    return cached ? { ...cached } : null
  }
  if (newsDetailPromise.has(key)) {
    return newsDetailPromise.get(key).then((d) => (d ? { ...d } : null))
  }

  const tryPaths = [`/api/v1/news/item/${id}`, `/api/v1/news/${id}`, `/api/v1/new/${id}`]
  const req = (async () => {
    for (const url of tryPaths) {
      try {
        const { data } = await http.get(url)
        const raw = data?.data !== undefined ? data.data : data
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const d = normalizeNews(raw)
          if (d) return d
        }
      } catch (e) {
        if (url === tryPaths[tryPaths.length - 1]) {
          console.warn('[news] 详情接口失败', id, e)
        }
      }
    }

    const local = FALLBACK_NEWS.find((n) => String(n.id) === String(id))
    return local ? { ...local } : null
  })()
    .then((detail) => {
      newsDetailCache.set(key, detail)
      return detail
    })
    .finally(() => {
      newsDetailPromise.delete(key)
    })

  newsDetailPromise.set(key, req)
  return req.then((d) => (d ? { ...d } : null))
}
